<template>
  <el-form :model="form" ref="productTagsForm">
    <fieldset class="flex flex-col border rounded-lg p-4 space-y-4">
      <legend class="px-2 text-sm font-medium text-left">
        Add Products and ESL Tags
      </legend>
      <div
        class="flex flex-col border rounded-lg p-4"
        v-for="(item, idx) in form"
        :key="idx"
      >
        <el-form-item
          label="Product"
          :prop="`${idx}.product_code`"
          :rules="[
            {
              required: true,
              message: 'Product is required',
              trigger: 'change',
            },
          ]"
        >
          <SmartSelect
            v-model="item.product_code"
            valueType="string"
            placeholder="Select Product"
            :load-method="getEslProducts"
            valueField="product_code"
            displayField="product_name"
            :pageSize="Number.MAX_SAFE_INTEGER"
            :pageFieldMap="{ page: 'f1', pageSize: 'f2' }"
            style="width: 100%"
            @select="(product) => handleProductChange(product, item)"
          />
        </el-form-item>
        <el-form-item
          label="ESL Tags"
          :prop="`${idx}.esl_code`"
          :rules="[
            {
              required: true,
              message: 'At least one ESL tag is required',
              trigger: 'blur',
            },
          ]"
        >
          <SmartSelect
            v-model="item.esl_code"
            multiple
            valueType="array<string>"
            placeholder="Select ESL Tags"
            :load-method="getEslTags"
            valueField="esl_code"
            displayField="esl_code"
            :pageSize="Number.MAX_SAFE_INTEGER"
            :pageFieldMap="{ page: 'f1', pageSize: 'f2' }"
            style="width: 100%"
          />
        </el-form-item>
        <fieldset class="border rounded-xl px-4">
          <legend class="font-bold text-left">Start Action</legend>
          <div class="grid grid-cols-3 gap-x-2">
            <el-form-item
              label="Price(#pp)"
              :prop="`${idx}.start_action.product_price`"
            >
              <el-input-number
                v-model="item.start_action.product_price"
                :precision="2"
                placeholder="Price"
                style="width: 100%"
              />
            </el-form-item>
            <el-form-item
              label="Discount Price(#vp)"
              :prop="`${idx}.start_action.product_vip_price`"
            >
              <el-input-number
                v-model="item.start_action.product_vip_price"
                :precision="2"
                placeholder="Discount Price"
                style="width: 100%"
              />
            </el-form-item>
            <el-form-item
              label="Origin Price(#pop)"
              :prop="`${idx}.start_action.product_origin_price`"
            >
              <el-input-number
                v-model="item.start_action.product_origin_price"
                :precision="2"
                placeholder="Origin Price"
                style="width: 100%"
              />
            </el-form-item>
            <el-form-item
              label="Template"
              :prop="`${idx}.start_action.template_id`"
              class="col-span-3"
              :rules="[
                {
                  required: true,
                  message: 'Start Action Template is required',
                  trigger: 'change',
                },
              ]"
            >
              <SmartSelect
                v-model="item.start_action.template_id"
                valueType="string"
                placeholder="Select Template"
                :load-method="getEslTemplates"
                valueField="id"
                displayField="esltemplate_name"
                :pageSize="Number.MAX_SAFE_INTEGER"
                :pageFieldMap="{ page: 'f1', pageSize: 'f2' }"
                style="width: 100%"
              />
            </el-form-item>
          </div>
        </fieldset>
        <fieldset class="border rounded-xl px-4 mt-4">
          <legend class="font-bold text-left">End Action</legend>
          <div class="grid grid-cols-3 gap-x-2">
            <el-form-item
              label="Price(#pp)"
              :prop="`${idx}.end_action.product_price`"
            >
              <el-input-number
                v-model="item.end_action.product_price"
                :precision="2"
                placeholder="Price"
                style="width: 100%"
              />
            </el-form-item>
            <el-form-item
              label="Discount Price(#vp)"
              :prop="`${idx}.end_action.product_vip_price`"
            >
              <el-input-number
                v-model="item.end_action.product_vip_price"
                :precision="2"
                placeholder="Discount Price"
                style="width: 100%"
              />
            </el-form-item>
            <el-form-item
              label="Origin Price(#pop)"
              :prop="`${idx}.end_action.product_origin_price`"
            >
              <el-input-number
                v-model="item.end_action.product_origin_price"
                :precision="2"
                placeholder="Origin Price"
                style="width: 100%"
              />
            </el-form-item>
            <el-form-item
              label="Template"
              :prop="`${idx}.end_action.template_id`"
              class="col-span-3"
              :rules="[
                {
                  required: true,
                  message: 'End Action Template is required',
                  trigger: 'change',
                },
              ]"
            >
              <SmartSelect
                v-model="item.end_action.template_id"
                valueType="string"
                placeholder="Select Template"
                :load-method="getEslTemplates"
                valueField="id"
                displayField="esltemplate_name"
                :pageSize="Number.MAX_SAFE_INTEGER"
                :pageFieldMap="{ page: 'f1', pageSize: 'f2' }"
                style="width: 100%"
              />
            </el-form-item>
          </div>
        </fieldset>
        <el-button
          v-if="form.length > 1"
          type="danger"
          icon="fa fa-trash"
          plain
          class="mt-2"
          @click="form.splice(idx, 1)"
          style="width: 100%"
        >
          Remove
        </el-button>
      </div>
      <el-button
        type="primary"
        icon="fa fa-plus"
        plain
        class="w-full"
        @click="handleAddProductAndTags"
      >
        Add Product and Tags
      </el-button>
    </fieldset>
  </el-form>
</template>

<script>
import {
  PriceTaskItemModel,
  getEslProducts,
  getEslTags,
  getEslTemplates,
} from "@/api";
import DateTimeRangePicker from "@/components/DateTimeRangePicker.vue";
import SmartSelect from "@/components/SmartControls/SmartSelect.vue";
export default {
  name: "ProductAndESLTags",
  components: { DateTimeRangePicker, SmartSelect },
  props: {
    value: {
      type: Array,
      default: () => [new PriceTaskItemModel()],
    },
  },
  data() {
    return {
      form: this.value,
      rules: {
        product_code: [
          { required: true, message: "Product is required", trigger: "change" },
        ],
        esl_code: [
          {
            required: true,
            message: "ESL Tags is required",
            trigger: "change",
          },
        ],
        start_action_template_id: [
          {
            required: true,
            message: "Start Action Template is required",
            trigger: "change",
          },
        ],
        end_action_template_id: [
          {
            required: true,
            message: "End Action Template is required",
            trigger: "change",
          },
        ],
      },
    };
  },
  methods: {
    getEslProducts: getEslProducts,
    getEslTags: getEslTags,
    getEslTemplates: getEslTemplates,
    handleAddProductAndTags() {
      const newItem = new PriceTaskItemModel();
      this.form.push(newItem);
      this.setDynamicRules();
    },
    handleProductChange(product, item) {
      if (product) {
        item.start_action.product_price = product.rawData.product_price;
        item.start_action.product_vip_price = product.rawData.product_vip_price;
        item.start_action.product_origin_price =
          product.rawData.product_origin_price;
        item.end_action.product_price = product.rawData.product_price;
        item.end_action.product_vip_price = product.rawData.product_vip_price;
        item.end_action.product_origin_price =
          product.rawData.product_origin_price;
      }
    },
    async validate() {
      await this.$refs.productTagsForm.validate();
    },
  },
  watch: {
    value: {
      handler(val) {
        this.form = val;
      },
    },
    form: {
      deep: true,
      handler(val) {
        this.$emit("input", val);
      },
    },
  },
  mounted() {
    if (this.form.length === 0) {
      this.form.push(new PriceTaskItemModel());
    }
  },
};
</script>
